import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronRight, Lock } from "lucide-react";

import { ACT_LABELS, FINAL_ACT, actNumber } from "@/components/projects/diagnostic/acts";
import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import type { DiagnosticDetail } from "@/components/projects/diagnostic/details";

/**
 * A moldura comum das telas de detalhe do Diagnóstico (#1339).
 *
 * Breadcrumb "Diagnóstico › Ato N · <ato> › <título>" e o botão "Voltar ao
 * diagnóstico", iguais para todas as EFs. O detalhe em si só recebe os dados.
 *
 * **A trava do Ato 5 mora aqui, e não em cada detalhe.** Detalhe com
 * `requiresReveal` nem monta o conteúdo antes da revelação: o número não chega
 * ao DOM, e a EF que esquecer de se travar não vaza nada. Detalhe que nasce no
 * Ato 5 tranca sempre, com ou sem a marca: o conteúdo daquele ato é o número.
 *
 * Ao abrir, o foco vai para o título. A célula que abriu desmontou, e sem isto
 * o foco cairia no `body` sem anunciar que a página mudou.
 */
export function DiagnosticDetailLayout({
  detail,
  revealed,
  onBack,
  ...tabProps
}: DiagnosticTabProps & {
  detail: DiagnosticDetail;
  revealed: boolean;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const locked = (detail.requiresReveal || detail.originAct === FINAL_ACT) && !revealed;
  const { Component } = detail;

  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, [detail.slug]);

  return (
    <div data-testid="diagnostic-detail" className="p-8">
      <nav aria-label={t("Breadcrumb")} className="mb-4">
        <ol className="flex flex-wrap items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-[#7b8799]">
          <li>{t("Diagnostic")}</li>
          <li aria-hidden>
            <ChevronRight className="h-3 w-3" />
          </li>
          <li>
            {t("Act {{act}} · {{label}}", {
              act: actNumber(detail.originAct),
              label: t(ACT_LABELS[detail.originAct].label),
            })}
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3 w-3" />
          </li>
          <li aria-current="page" className="text-[#08254c]">
            {t(detail.title)}
          </li>
        </ol>
      </nav>

      <button
        type="button"
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-2 rounded-md border border-[#d4dae3] px-3.5 py-2 text-[13px] font-semibold text-[#596b85] transition-colors hover:border-[#08254c] hover:text-[#08254c]"
      >
        <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
        {t("Back to the diagnostic")}
      </button>

      <h2
        ref={titleRef}
        tabIndex={-1}
        className="mb-4 text-[20px] font-semibold text-[#08254c] focus:outline-none"
      >
        {t(detail.title)}
      </h2>

      {locked ? (
        <div
          data-testid="diagnostic-detail-locked"
          className="flex items-center gap-2 rounded-md border border-dashed border-[#d4dae3] px-4 py-6 text-[13px] text-[#596b85]"
        >
          <Lock aria-hidden className="h-4 w-4" />
          {t("This detail opens together with the numbers, in Investment.")}
        </div>
      ) : (
        <Component {...tabProps} />
      )}
    </div>
  );
}
